import { useState } from 'react';
import styles from './TraceHuntConfig.module.css';
import { useTheme } from '../../../hooks/useTheme';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { getAdminUserConfig, setAdminUserConfig, UserConfig } from '../../../services/tracehuntService';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Input, Button } from '@dsi/react-eds';

// Extract the most meaningful error message: prefer the backend's `detail`
// (e.g. FastAPI HTTPException), then a nested message, then the axios/Error
// message, falling back to a provided default.
function extractErrorMessage(err: unknown, fallback: string): string {
  const response = (err as { response?: { data?: unknown } })?.response;
  const data = response?.data;
  if (data && typeof data === 'object') {
    const detail = (data as { detail?: unknown; message?: unknown }).detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    const message = (data as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  if (typeof data === 'string' && data.trim()) return data;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function TraceHuntConfig() {
  const { theme } = useTheme();
  const { addNotifications } = useSwobNotifications();

  // User Config
  const [cfgUsername, setCfgUsername] = useState('');
  const [maxThreads, setMaxThreads] = useState('');
  const [maxParallel, setMaxParallel] = useState('');
  const [cfgMsg, setCfgMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const handleLoadConfig = async () => {
    if (!cfgUsername.trim()) {
      setCfgMsg({ ok: false, text: 'Enter a username.' });
      return;
    }
    
    try {
      const data = await getAdminUserConfig(cfgUsername.trim());
      const cfg = data.user_config || {};
      setMaxThreads(String(cfg.max_threads_per_task ?? ''));
      setMaxParallel(String(cfg.max_parallel_tasks ?? ''));
      setCfgMsg({ ok: true, text: `Loaded config for "${cfgUsername.trim()}"` });
    } catch (err) {
      const message = extractErrorMessage(err, 'Failed to load configuration');
      setCfgMsg({ ok: false, text: message });
      addNotifications([
        {
          title: 'TraceHunt Config Error',
          icon: 'icon-triangle-warning',
          description: message,
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    }
  };

  const handleSaveConfig = async () => {
    if (!cfgUsername.trim()) {
      setCfgMsg({ ok: false, text: 'Enter a username.' });
      return;
    }
    
    try {
      const userConfig: UserConfig = {
        max_threads_per_task: parseInt(maxThreads) || 1,
        max_parallel_tasks: parseInt(maxParallel) || 1,
      };
      
      await setAdminUserConfig(cfgUsername.trim(), userConfig);
      setCfgMsg({ ok: true, text: `Saved config for "${cfgUsername.trim()}"` });
      addNotifications([
        {
          title: 'TraceHunt Config Saved',
          icon: 'icon-check',
          description: `Configuration saved for user "${cfgUsername.trim()}"`,
          timestamp: new Date(),
          color: 'green'
        }
      ]);
    } catch (err) {
      const message = extractErrorMessage(err, 'Failed to save configuration');
      setCfgMsg({ ok: false, text: message });
      addNotifications([
        {
          title: 'TraceHunt Config Error',
          icon: 'icon-triangle-warning',
          description: message,
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    }
  };

  const handleCancel = () => {
    setCfgUsername('');
    setMaxThreads('');
    setMaxParallel('');
    setCfgMsg(null);
  };

  return (
    <div className={`${theme ? 'light-theme' : 'dark-theme'} ${styles.container}`}>
      <p className={styles.description}>
        Configure TraceHunt processing limits per user. Load current values, edit and save them.
        Users are provisioned on first login; loading an unknown user returns no configuration.
      </p>

      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '300px' }}>
          <div style={{ width: '100%', minWidth: '250px' }}>
            <ReactEDSWrapper 
              component={Input} 
              label="Username *"
              placeholder="e.g. jdoe" 
              value={cfgUsername} 
              onChange={(e) => setCfgUsername((e.target as HTMLInputElement).value)}
              style={{ width: '100%' }}
            />
            <p className={styles.fieldHint}>
              The user whose TraceHunt processing limits you are viewing or editing.
            </p>
          </div>
          <div style={{ width: '100%', minWidth: '250px' }}>
            <ReactEDSWrapper
              component={Input}
              type="number"
              label="Max threads per task *"
              placeholder="e.g. 4"
              value={maxThreads}
              onChange={(e) => setMaxThreads((e.target as HTMLInputElement).value)}
              min="1"
              style={{ width: '100%' }}
            />
            <p className={styles.fieldHint}>
              Number of parallel threads used to process a single task. Higher values
              speed up individual tasks but use more resources. Minimum 1.
            </p>
          </div>
          <div style={{ width: '100%', minWidth: '250px' }}>
            <ReactEDSWrapper
              component={Input}
              type="number"
              label="Max parallel tasks *"
              placeholder="e.g. 2"
              value={maxParallel}
              onChange={(e) => setMaxParallel((e.target as HTMLInputElement).value)}
              min="1"
              style={{ width: '100%' }}
            />
            <p className={styles.fieldHint}>
              How many tasks this user can run at the same time. Higher values increase
              throughput but consume more system capacity. Minimum 1.
            </p>
          </div>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: '150px', alignItems: 'stretch' }}>
          <ReactEDSWrapper 
            component={Button} 
            variant="secondary" 
            onClick={handleLoadConfig}
            style={{ width: '100%', margin: '0' }}
          >
            Load Configuration
          </ReactEDSWrapper>
          
          <ReactEDSWrapper 
            component={Button} 
            variant="secondary" 
            onClick={handleCancel}
            style={{ width: '100%', margin: '0' }}
          >
            Cancel
          </ReactEDSWrapper>
          
          <ReactEDSWrapper 
            component={Button} 
            variant="primary" 
            onClick={handleSaveConfig}
            style={{ width: '100%', margin: '0' }}
          >
            Save
          </ReactEDSWrapper>
        </div>
      </div>

      {cfgMsg && (
        <p className={cfgMsg.ok ? styles.messageOk : styles.messageErr}>
          {cfgMsg.text}
        </p>
      )}
    </div>
  );
}

export default TraceHuntConfig;